<template>
  <div id="editor">
    <div>
      <div class="editor-header d-flex flex-sm-row flex-column flex-wrap mr-sm-13">
        <file-toolbar
          id="file_toolbar_wrapper"
          ref="filetoolbar"
          class="mb-lg-0 mb-3 file-toolbar-wrapper mr-sm-4 mr-0"
        />
        <mobile-global-toolbar v-show="xs" ref="mobileGlobalToolbar" class="mr-4 mb-3" />
        <div
          v-show="showPaperToolbar"
          id="papertoolbar-items-wrapper"
          class="drag-and-drop-items-wrapper ml-4 ml-sm-0 mr-4 mb-3"
        >
          <paper-toolbar ref="papertoolbar" />
        </div>
        <div
          v-show="showSimulationView"
          id="simulation-view-items-wrapper"
          class="drag-and-drop-items-wrapper ml-4 ml-sm-0 mr-4 mb-3"
          :class="{ unclickable: !hasSimulationFeature }"
        >
          <simulation-view ref="simulationview" />
        </div>
        <div id="dragable_card_wrapper" ref="dragablecard" class="drag-and-drop-items-wrapper ml-4 ml-sm-0 mr-4 mb-3">
          <dragable-card v-if="subdiagramsEnabled && breadcrumbItems.length > 1">
            <subdiagram-breadcrumb
              ref="subdiagrambreadcrumb"
              :items="breadcrumbItems"
              @select="handleBreadcrumb"
            />
          </dragable-card>
        </div>
        <!--<theme-toolbar ref="themetoolbar" />-->
      </div>
      <div v-show="mobileDisplayedView === 'statechartDefinitions' || (smAndUp && height >= 565)">
        <statechart-definitions-view />
      </div>
    </div>
    <settings-toolbar id="user_toolbar_wrapper" class="user-toolbar-wrapper mb-3" />
    <div v-show="mobileDisplayedView === 'palette' || (smAndUp && height >= 565)">
      <statechart-palette ref="palette" :right="paletteRight" :transition="compTransition" />
    </div>
    <mini-map v-show="mdAndUp" ref="minimap" :right="minimapRight" :transition="compTransition" />
    <div :class="{ unclickable: !hasCopilotFeature }">
      <chat-container v-if="copilotService.isEnabled()" v-model="openCopilot" @resize="onCopilotResize" />
    </div>
    <div v-show="mobileDisplayedView === 'properties' || (smAndUp && height >= 565)">
      <properties-view />
    </div>
    <problem-view />
    <mobile-views-toggler v-if="xs || height < 565" :view="mobileDisplayedView" />
    <statechart-canvas></statechart-canvas>
    <div class="canvas-step"></div>
  </div>
</template>

<script setup lang="ts">
import StatechartPalette from "./StatechartPalette.vue";
import MiniMap from "./MiniMap.vue";
import StatechartCanvas from "./StatechartCanvas.vue";
import FileToolbar from "./FileToolbar/FileToolbar.vue";
import PaperToolbar from "./PaperToolbar.vue";
import MobileGlobalToolbar from "./MobileGlobalToolbar.vue";
import PropertiesView from "./PropertiesView/PropertiesView.vue";
import ProblemView from "./ProblemView/ProblemView.vue";
import StatechartDefinitionsView from "./StatechartDefinitionsView/StatechartDefinitionsView.vue";
import SettingsToolbar from "../SettingsToolbar/SettingsToolbar.vue";
import MobileViewsToggler from "./MobileViewsToggler.vue";
import SimulationView from "../SimulationView/SimulationView.vue";
import SubdiagramBreadcrumb from "./SubdiagramBreadcrumb.vue";
import { MessageEventBus, ProgressBarEventBus, UIEventBus } from "../event-bus";
import { DirtyState } from "./services/dirty-state";
import LocalStorage from "./services/persistance/local-storage";
import UserInterface from "./services/ui/user-interface";
import ModelLoader from "./services/model-loader";
import { Ref, inject, onBeforeUnmount, onMounted, ref, watch, computed } from "vue";
import { useDisplay } from "vuetify";
import RecentItemsService from "./services/storage/recent-items-service";
import { StatechartsService } from "./services/statecharts/statecharts-service";
import { loadLastModel, loadAndShowSuccess } from "./utils/loadStatechart";
import ChatContainer from "./chat/ChatContainer.vue";
import { CopilotService } from "./services/copilot/copilot-service";
import { CodeSimulationService } from "./services/simulation/code/simulation-service-code";
import router from "../../router";
import { useUserStore } from "../../components/Authentication/store/userStore";
import { CREATEFeature } from "../../components/TermsAndConditions/CREATEFeature";
import RappidContext from "./rappid/rappid-context";
import { activeView, getSubdiagramBreadcrumbItems, updateVisibleStates } from "./subdiagram-viewer";
import { getConfig } from "../../config";
import { SubdiagramBreadcrumbItem } from "./types";
import DragableCard from "./DragableCard.vue";
import { deselectAll } from "./mvc/base/utils";

const { subdiagramsEnabled } = getConfig();

const simulationControl: CodeSimulationService = inject<CodeSimulationService>("simulationControl");

const dirtyState: DirtyState = inject<DirtyState>("dirtyState");

const recentItemsService: RecentItemsService = inject<RecentItemsService>("recentItemsService");

const localStorage: LocalStorage = inject<LocalStorage>("localStorage");

const userInterface: UserInterface = inject<UserInterface>("userInterface");

const modelLoader: ModelLoader = inject<ModelLoader>("modelLoader");

const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");
const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const copilotService = inject<CopilotService>("copilotService");
const openCopilot = ref<boolean>(false);
const minimapRight = ref<string>(undefined);
const paletteRight = ref<string>(undefined);
const compTransition = ref<string>("right 0.4s ease-in-out");

const userStore = useUserStore();
const hasCopilotFeature = computed(
  () => userStore.features.includes(CREATEFeature.AI_COPILOT) && userStore.remainingDays > 0
);
const hasSimulationFeature = computed(
  () => userStore.features.includes(CREATEFeature.SIMULATION) && userStore.remainingDays > 0
);
const showPaperToolbar = ref(false);
const showSimulationView = ref(false);
const mobileDisplayedView = ref<"palette" | "properties" | "statechartDefinitions">("palette");

const { mdAndUp, smAndUp, xs, height } = useDisplay();

watch(smAndUp, (newVal: boolean) => {
  showPaperToolbar.value = showSimulationView.value = newVal;
});

watch(openCopilot, (newVal: boolean) => {
  if (newVal) {
    updateCoPilotPositions();
  } else {
    //turn transition back on
    compTransition.value = "right 0.4s ease-in-out";
    if (compShouldMove(".guide-minimap-container")) {
      minimapRight.value = undefined;
    }
    if (compShouldMove(".palette-container")) {
      paletteRight.value = undefined;
    }
  }
});

function updateCoPilotPositions() {
  setPositionDepOnCopilot(".guide-minimap-container", minimapRight);
  setPositionDepOnCopilot(".palette-container", paletteRight);
}

function onCopilotResize() {
  if (openCopilot.value) {
    //turn transition off
    compTransition.value = "none";
    updateCoPilotPositions();
  }
}

function setPositionDepOnCopilot(selector: ".guide-minimap-container" | ".palette-container", right: Ref<string>) {
  const copilotRect = document.querySelector(".copilot-card").getBoundingClientRect();

  if (compShouldMove(selector)) {
    right.value = `calc(${copilotRect.width}px + 30px)`;
  }
}

function compShouldMove(selector: ".guide-minimap-container" | ".palette-container"): boolean {
  const copilotRect = document.querySelector(".copilot-card").getBoundingClientRect();

  const copilotLeftDistance = window.innerWidth - copilotRect.width;

  const compRect = document.querySelector(selector).getBoundingClientRect();

  // move component only if on height level of copilot and if close to it
  return compRect.right > copilotLeftDistance - 300 && compRect.bottom >= copilotRect.top;
}

const updateView = (view: "palette" | "properties" | "statechartDefinitions") => {
  mobileDisplayedView.value = view;
};

const breadcrumbItems = computed(() => getSubdiagramBreadcrumbItems());

function handleBreadcrumb(item: SubdiagramBreadcrumbItem, index: number) {
  updateVisibleStates(rappidContext, item.stateView);
  deselectAll(rappidContext, true);
}

onMounted(async () => {
  userInterface.init();
  localStorage.startListening();
  localStorage.loadIntoPreferenceStore();

  showPaperToolbar.value = smAndUp.value;
  showSimulationView.value = smAndUp.value;
  UIEventBus.on("updateView", updateView);
  UIEventBus.on("toolbarToggled", (toolbar) => {
    if (!smAndUp.value) {
      switch (toolbar) {
        case "edit":
          showPaperToolbar.value = !showPaperToolbar.value;
          showSimulationView.value = false;
          break;
        case "simulation":
          showSimulationView.value = !showSimulationView.value;
          showPaperToolbar.value = false;
          break;
      }
    }
  });

  await loadInitialModel();
  updateVisibleStates(rappidContext, null);

  dirtyState.activate();
});

onBeforeUnmount(() => {
  UIEventBus.off("updateView", updateView);
  UIEventBus.off("toolbarToggled", toggleToolbar);
});

function toggleToolbar(toolbar: "simulation" | "edit") {
  if (!smAndUp.value) {
    switch (toolbar) {
      case "edit":
        showPaperToolbar.value = !showPaperToolbar.value;
        showSimulationView.value = false;
        break;
      case "simulation":
        showSimulationView.value = !showSimulationView.value;
        showPaperToolbar.value = false;
        break;
    }
  }
}

async function loadInitialModel() {
  let modelId: string;

  if (window.localStorage) {
    const urlParams = new URLSearchParams(window.localStorage.getItem("sct_queryParams"));
    // reset urlParams after they got read
    window.localStorage.setItem("sct_queryParams", "");

    if (urlParams.has("type") && urlParams.get("type") === "player" && urlParams.has("model")) {
      await cloudStatechartService
        .getModelIdFromPlayer(urlParams.get("model"))
        .then((res) => {
          modelId = res;
        })
        .catch(urlLoadingFailed);
    } else if (urlParams.has("model")) {
      modelId = urlParams.get("model");
    }
  }

  if (modelId) {
    await loadAndShowSuccess(
      cloudStatechartService,
      simulationControl,
      modelLoader,
      modelId,
      "url",
      undefined
    ).catch(async () => {
      urlLoadingFailed();
      await loadLastModel(
        cloudStatechartService,
        simulationControl,
        recentItemsService,
        modelLoader,
        dirtyState,
        localStorage
      ).catch(() => ProgressBarEventBus.emit("finishedTask", "initial-loading"));
    });
  } else {
    await loadLastModel(
      cloudStatechartService,
      simulationControl,
      recentItemsService,
      modelLoader,
      dirtyState,
      localStorage
    ).catch(() => ProgressBarEventBus.emit("finishedTask", "initial-loading"));
  }

  ProgressBarEventBus.emit("finishedTask", "initial-loading");
}

function urlLoadingFailed() {
  // get rid of url params to not create on every reload of page a new model
  router.push({ path: "/home" });
  MessageEventBus.emit("error", {
    text: "Failed to load statechart requested via URL.",
    id: "statechart-editor",
  });
}
</script>

<style>
.copilot-card,
.guide-minimap-container,
.palette-container {
  position: fixed;
  z-index: 1;
}

.copilot-card {
  top: 60px;
  bottom: 20px;
}

.guide-minimap-container {
  padding: 0px;
}
</style>

<style scoped>
.canvas-step {
  position: absolute;
  color: black;
  bottom: 30vh;
  left: 50vw;
  z-index: 2;
}
.unclickable {
  pointer-events: none !important;
  opacity: 0.6;
}

.drag-and-drop-items-wrapper {
  position: static;
  height: 48px;
}
.drag-and-drop-items-wrapper#papertoolbar-items-wrapper {
  min-width: 315px;
}
.drag-and-drop-items-wrapper#simulation-view-items-wrapper {
  min-width: 200px;
}
.mobile-action-toggler {
  position: absolute;
  z-index: 9;
  bottom: 4px;
  right: 0;
}
@media (max-width: 959.98px) {
  .drag-and-drop-items-wrapper#simulation-view-items-wrapper {
    min-width: 175px;
  }
  .drag-and-drop-items-wrapper#papertoolbar-items-wrapper {
    min-width: 325px;
  }
}
</style>
